import { Impact, isTree, SUBPROFILE_TILE_PROJECT_ID } from "@ecologi/shared";
import styled from "@emotion/styled";
import Image from "next/image";
import React from "react";
import { BIRTHDAY_BLOG_ARTICLES, DIVIDEND_BLOG_URL } from "./consts";
import { getPictogramUrl } from "./utils";

const BoldImpactAmount = styled.span`
  font-weight: bold;
`;

const InfoCard = styled.div`
  display: flex;
  gap: 0 12px;
  border-radius: 16px;
  padding: 20px;
  margin-top: 24px;
  line-height: 1.5;
  background-color: ${({ theme }) => theme.colors.mushroomLight};
  min-height: 80px;

  @media ${({ theme }) => theme.bp.mobileDown} {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const ModalLink = styled.a`
  border-bottom: 1px solid ${({ theme }) => theme.colors.night};
  color: ${({ theme }) => theme.colors.night};
  hyphens: auto;
  text-decoration: none;

  &:hover {
    border-bottom-color: ${({ theme }) => theme.colors.jungle};
    color: ${({ theme }) => theme.colors.jungle};
  }
`;

const Pictogram = styled.div`
  position: relative;
  width: 56px;
  flex-shrink: 0;
  max-height: 50px;

  @media ${({ theme }) => theme.bp.mobileDown} {
    height: 56px;
    margin-bottom: 12px;
  }
`;

const ProjectImpactTextWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
`;

const SpecialTreeText = styled.p`
  font-weight: bold;
`;

export const ModalInfoCard = ({
  impact,
  isOwnProfile,
}: {
  impact: Impact;
  isOwnProfile: boolean;
}) => {
  const pictogramUrl = getPictogramUrl(impact);

  if (!isTree(impact)) {
    const description = impact.project?.impactFact;

    return (
      <>
        {description && (
          <InfoCard data-testid="impact-info-card">
            <Pictogram>
              {pictogramUrl && (
                <Image
                  alt={`${impact.project?.title} pictogram`}
                  fill
                  src={pictogramUrl}
                  unoptimized
                />
              )}
            </Pictogram>
            <ProjectImpactTextWrapper data-testid="forest-modal-blurb">
              {description}
            </ProjectImpactTextWrapper>
          </InfoCard>
        )}
      </>
    );
  }

  if (impact.localProject || impact.isBirthday || impact.isDividend) {
    return (
      <InfoCard>
        <Pictogram>
          {pictogramUrl && (
            <Image
              alt={`${
                impact.localProject
                  ? `Flag of ${impact.localProject.countryNiceCopy}`
                  : impact.isBirthday
                  ? "Birthday tree pictogram"
                  : "Dividend tree pictogram"
              }`}
              fill
              src={pictogramUrl}
              unoptimized
            />
          )}
        </Pictogram>

        <ProjectImpactTextWrapper data-testid="forest-modal-blurb">
          {impact.localProject &&
            `Supporting local reforestation in ${impact.localProject.countryNiceCopy}`}

          {(impact.isBirthday || impact.isDividend) && (
            <>
              <SpecialTreeText>
                {impact.isBirthday &&
                  BIRTHDAY_BLOG_ARTICLES[impact.treeYear].title}
                {impact.isDividend
                  ? isOwnProfile
                    ? "Your tree dividend"
                    : "Tree dividend"
                  : null}
              </SpecialTreeText>

              <p>
                {`More info in our `}
                <ModalLink
                  href={
                    impact.isBirthday
                      ? BIRTHDAY_BLOG_ARTICLES[impact.treeYear].blogUrl
                      : DIVIDEND_BLOG_URL
                  }
                  target="_blank"
                  title="Read our blog post (new tab)"
                  rel="noopener"
                >
                  blog post
                </ModalLink>
              </p>
            </>
          )}
        </ProjectImpactTextWrapper>
      </InfoCard>
    );
  }

  if (impact.projectId === SUBPROFILE_TILE_PROJECT_ID) return null;

  return (
    <InfoCard data-testid="tree-info-card">
      <p>
        {`Your contribution helps fund all the projects within our global reforestation mix. This tile represents trees in one of these projects. `}
        <ModalLink
          href={"/articles/blog/where-are-my-trees"}
          rel="noopener"
          target="_blank"
        >
          Learn more
        </ModalLink>
      </p>
    </InfoCard>
  );
};
